import React, { useEffect, useRef, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, BarChart, Bar } from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { Send, Play, Volume2, Loader2, BookOpen, Link2, Upload, Trash2, FileText, Globe, Image as ImageIcon, Table, MessageCircleQuestion, PhoneCall, Save, RotateCcw, ChevronDown, History, CheckCircle2, FlaskConical, Terminal } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { LANG_NAMES } from "@/lib/format";
import api, { apiErr } from "@/lib/api";

function KnowledgeTab({ onTest }) {
  const [sources, setSources] = useState([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [ask, setAsk] = useState([]); // {role, text, grounded}
  const [asking, setAsking] = useState(false);
  const fileRef = React.useRef(null);
  const load = () => api.get("/knowledge").then((r) => setSources(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const addUrl = async () => { if (!url.trim()) return; setBusy(true); try { await api.post("/knowledge/url", { url }); toast.success("Website added to knowledge base"); setUrl(""); load(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const upload = async (e) => { const f = e.target.files?.[0]; if (!f) return; setBusy(true); try { const fd = new FormData(); fd.append("file", f); await api.post("/knowledge/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${f.name} added`); load(); } catch (e2) { toast.error(apiErr(e2)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; } };
  const del = async (id) => { await api.delete(`/knowledge/${id}`); toast.success("Removed"); load(); };
  const doAsk = async () => {
    if (!q.trim()) return;
    const question = q.trim();
    setAsk((p) => [...p, { role: "user", text: question }]); setQ(""); setAsking(true);
    try {
      const { data } = await api.post("/knowledge/ask", { question });
      setAsk((p) => [...p, { role: "agent", text: data.reply, grounded: data.grounded }]);
    } catch (e) { toast.error(apiErr(e)); } finally { setAsking(false); }
  };
  const ICON = { website: Globe, pdf: FileText, docx: FileText, xlsx: Table, image: ImageIcon, text: FileText };
  return (
    <div className="space-y-6">
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="border shadow-none p-6 lg:col-span-1 space-y-4">
        <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-indigo-500" /><h3 className="font-semibold font-display">Add Knowledge</h3></div>
        <p className="text-xs text-slate-500">Akriti answers callers using these sources — website, PDF, Word, Excel, image or a URL.</p>
        <div>
          <Label className="text-xs">Website / URL</Label>
          <div className="flex gap-2 mt-1"><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yoursite.com/products" data-testid="kb-url" /><Button onClick={addUrl} disabled={busy} data-testid="kb-add-url"><Link2 className="w-4 h-4" /></Button></div>
        </div>
        <div>
          <Label className="text-xs">Upload file</Label>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.webp" onChange={upload} className="hidden" data-testid="kb-file-input" />
          <Button variant="outline" className="w-full mt-1" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="kb-upload-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} PDF · Word · Excel · Image</Button>
        </div>
      </Card>
      <Card className="border shadow-none lg:col-span-2">
        <div className="p-5 border-b flex items-center justify-between"><h3 className="font-semibold font-display">Knowledge Sources</h3><span className="text-xs text-slate-400">{sources.length} items</span></div>
        {sources.length === 0 ? <div className="p-10 text-center text-sm text-slate-400" data-testid="kb-empty">No knowledge yet — add a website or upload a document.</div> :
          <div className="divide-y">{sources.map((s) => { const Ic = ICON[s.type] || FileText; return (
            <div key={s.id} className="flex items-center gap-3 px-5 py-3" data-testid={`kb-source-${s.id}`}>
              <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center"><Ic className="w-4 h-4 text-indigo-500" /></div>
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{s.name}</div><div className="text-xs text-slate-400 capitalize">{s.type} · {s.chars} chars</div></div>
              <button onClick={() => del(s.id)} className="text-slate-400 hover:text-red-500" data-testid={`kb-del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>); })}
          </div>}
      </Card>

      <Card className="border shadow-none lg:col-span-3 flex flex-col" data-testid="kb-ask-card">
        <div className="p-5 border-b flex items-center gap-2">
          <MessageCircleQuestion className="w-4 h-4 text-emerald-500" />
          <div><h3 className="font-semibold font-display">Ask your knowledge base</h3><p className="text-xs text-slate-500">Test how Akriti answers customers using your knowledge — before you go live.</p></div>
        </div>
        <div className="p-5 space-y-3 min-h-[140px]">
          {ask.length === 0 && <div className="text-sm text-slate-400" data-testid="kb-ask-empty">Try: "What products do you offer?" or "Do you deliver to Pune?"</div>}
          {ask.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.role === "user" ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800"}`} data-testid={m.role === "agent" ? "kb-ask-reply" : undefined}>
                {m.text}
                {m.role === "agent" && (
                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${m.grounded ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"}`}>{m.grounded ? "grounded" : "no KB match"}</span>
                )}
              </div>
            </div>
          ))}
          {asking && <div className="text-xs text-slate-400">Akriti is thinking...</div>}
        </div>
        <div className="p-4 border-t flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doAsk()} placeholder="Ask a question your customer might ask..." data-testid="kb-ask-input" />
          <Button onClick={doAsk} disabled={asking || !q.trim()} data-testid="kb-ask-send"><Send className="w-4 h-4" /></Button>
        </div>
      </Card>
    </div>

      <CallingPromptSection onTest={onTest} />
    </div>
  );
}

const CALLING_PROMPT_PLACEHOLDER = `Write your complete AI calling instructions here...
Example:
You are [Agent Name], calling on behalf of [Company Name].
Company: [Describe your business in 2-3 lines]
Your goal: [What you want to achieve on each call]
Opening: [Exact words to say when someone picks up]
Questions to ask (one at a time):
1. [First question]
2. [Second question]
3. [Third question]
If not interested: [What to say]
If they ask about pricing: [What to say]
If they want callback: [What to say]
Always speak in Hindi. Switch to caller's language if they respond in Gujarati or English.`;

function fmtWhen(s) {
  if (!s) return "";
  return new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function CallingPromptSection({ onTest }) {
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [history, setHistory] = useState([]);
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [restoreIdx, setRestoreIdx] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const savedRef = useRef("");
  const textRef = useRef("");
  textRef.current = text;

  useEffect(() => {
    api.get("/agent/calling-prompt").then((r) => {
      setText(r.data.text || ""); savedRef.current = r.data.text || "";
      setLastSaved(r.data.updated_at || null); setHistory(r.data.history || []); setDefaultPrompt(r.data.default || "");
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const doSave = async (value, silent = false) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/agent/calling-prompt", { value });
      savedRef.current = data.text; setLastSaved(data.updated_at); setHistory(data.history || []);
      if (!silent) toast.success("Prompt saved");
      return true;
    } catch (e) { if (!silent) toast.error(apiErr(e)); return false; } finally { setSaving(false); }
  };

  // Auto-save every 60s if content changed
  useEffect(() => {
    const t = setInterval(() => {
      const v = textRef.current;
      if (v !== savedRef.current && v.trim()) doSave(v, true);
    }, 60000);
    return () => clearInterval(t);
  }, []);

  const dirty = text !== savedRef.current;
  const chars = text.length;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const minutes = (words / 130).toFixed(1);

  const doReset = async () => { setShowReset(false); setText(defaultPrompt); await doSave(defaultPrompt); };
  const doRestore = async () => { const v = history[restoreIdx]?.text || ""; setRestoreIdx(null); setText(v); await doSave(v); };
  const doTest = async () => { if (dirty && text.trim()) await doSave(text, true); onTest && onTest(); };

  if (!loaded) return <Card className="border shadow-none p-6"><Loader2 className="w-5 h-5 animate-spin text-indigo-500 mx-auto" /></Card>;

  return (
    <Card className="border shadow-none p-6" data-testid="calling-prompt-section">
      <div className="flex items-center gap-2 mb-1"><Terminal className="w-4 h-4 text-indigo-500" /><h3 className="font-semibold font-display">AI Calling Prompt</h3></div>
      <p className="text-xs text-slate-500 mb-4">Define exactly how Akriti should talk to your customers on calls. The more detailed you write, the better results you get.</p>

      <Label className="text-sm" htmlFor="calling-prompt-textarea">Your Custom Calling Prompt</Label>
      <textarea
        id="calling-prompt-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={CALLING_PROMPT_PLACEHOLDER}
        className="w-full mt-1.5 rounded-md border px-3 py-2 text-sm bg-transparent font-mono leading-relaxed"
        style={{ minHeight: "400px" }}
        data-testid="calling-prompt-textarea"
      />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-xs text-slate-500" data-testid="calling-prompt-stats">
        <span><span className="font-semibold text-slate-700 dark:text-slate-200" data-testid="cp-char-count">{chars}</span> characters</span>
        <span><span className="font-semibold text-slate-700 dark:text-slate-200" data-testid="cp-word-count">{words}</span> words</span>
        <span>Est. call coverage: <span className="font-semibold text-slate-700 dark:text-slate-200" data-testid="cp-minutes">{minutes}</span> min</span>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <Button onClick={() => doSave(text)} disabled={saving} data-testid="calling-prompt-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save Prompt
        </Button>
        <Button variant="outline" onClick={() => setShowReset(true)} data-testid="calling-prompt-reset"><RotateCcw className="w-4 h-4 mr-1" />Reset to Default</Button>
        <div className="ml-auto flex items-center gap-1.5 text-xs" data-testid="calling-prompt-save-indicator">
          {saving ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" /><span className="text-slate-500">Saving...</span></>
          ) : lastSaved ? (
            <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /><span className="text-slate-500">Last saved: {fmtWhen(lastSaved)}</span></>
          ) : null}
        </div>
      </div>

      <Collapsible open={histOpen} onOpenChange={setHistOpen} className="mt-6 border-t pt-4">
        <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium w-full" data-testid="calling-prompt-history-toggle">
          <History className="w-4 h-4 text-slate-400" />Prompt History (last 5 versions)
          <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${histOpen ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-2">
          {history.length === 0 ? (
            <div className="text-xs text-slate-400" data-testid="calling-prompt-history-empty">No previous versions yet. Older prompts appear here after you save changes.</div>
          ) : history.map((h, i) => (
            <div key={h.at || i} className="flex items-center gap-3 text-xs rounded-lg border p-2.5" data-testid={`calling-prompt-history-${i}`}>
              <span className="text-slate-400 whitespace-nowrap w-28 shrink-0">{fmtWhen(h.at)}</span>
              <span className="flex-1 min-w-0 truncate text-slate-600 dark:text-slate-300 font-mono">{(h.text || "").slice(0, 60)}...</span>
              <Button size="sm" variant="ghost" className="h-7 shrink-0" onClick={() => setRestoreIdx(i)} data-testid={`calling-prompt-restore-${i}`}>Restore</Button>
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>

      <div className="mt-6 border-t pt-4">
        <Button variant="outline" onClick={doTest} data-testid="calling-prompt-test"><FlaskConical className="w-4 h-4 mr-1 text-indigo-500" />Test This Prompt</Button>
        <p className="text-[11px] text-slate-400 mt-2">Saves your prompt and opens the Test tab so you can immediately try it on a simulated call.</p>
      </div>

      <AlertDialog open={showReset} onOpenChange={setShowReset}>
        <AlertDialogContent data-testid="calling-prompt-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to platform default?</AlertDialogTitle>
            <AlertDialogDescription>This will replace your custom prompt with the platform default. Are you sure?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="calling-prompt-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doReset} data-testid="calling-prompt-reset-confirm">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={restoreIdx !== null} onOpenChange={(o) => !o && setRestoreIdx(null)}>
        <AlertDialogContent data-testid="calling-prompt-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this version?</AlertDialogTitle>
            <AlertDialogDescription>Replace current prompt with this version?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="calling-prompt-restore-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRestore} data-testid="calling-prompt-restore-confirm">Restore</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

const AVATAR = "https://images.unsplash.com/photo-1607746882042-944635dfe10e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4Njc5OTI3Mnww&ixlib=rb-4.1.0&q=85";

export default function AIAgent() {
  const [agent, setAgent] = useState(null);
  const [perf, setPerf] = useState(null);
  const [tab, setTab] = useState("persona");

  useEffect(() => {
    api.get("/agent").then((r) => setAgent(r.data));
    api.get("/analytics/ai").then((r) => setPerf(r.data));
  }, []);

  const save = async () => { await api.patch("/agent", agent); toast.success("Agent settings saved"); };
  const set = (k, v) => setAgent((p) => ({ ...p, [k]: v }));

  if (!agent) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500 mx-auto" /></div>;

  return (
    <div>
      <PageHeader title="AI Agent" subtitle="Configure Akriti Patel — your AI sales caller">
        <Button onClick={save} data-testid="save-agent">Save Changes</Button>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto"><TabsTrigger value="persona">Persona</TabsTrigger><TabsTrigger value="outbound">Outbound Script</TabsTrigger><TabsTrigger value="inbound">Inbound Script</TabsTrigger><TabsTrigger value="knowledge" data-testid="tab-knowledge">Knowledge</TabsTrigger><TabsTrigger value="behaviour">Behaviour</TabsTrigger><TabsTrigger value="test" data-testid="tab-test">Test</TabsTrigger><TabsTrigger value="performance">Performance</TabsTrigger></TabsList>

        <TabsContent value="persona">
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="border shadow-none p-6 text-center">
              <img src={AVATAR} alt="Akriti" className="w-28 h-28 rounded-full object-cover mx-auto mb-4 ring-4 ring-indigo-100 dark:ring-indigo-500/20" />
              <h3 className="font-semibold font-display text-lg">{agent.name}</h3>
              <p className="text-sm text-slate-500">AI Sales Agent · {agent.company}</p>
            </Card>
            <Card className="border shadow-none p-6 lg:col-span-2 space-y-4">
              <div><Label>Agent Name</Label><Input value={agent.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div><Label>Company Name</Label><Input value={agent.company} onChange={(e) => set("company", e.target.value)} /></div>
              <div><Label>Voice (Sarvam)</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={agent.voice} onValueChange={(v) => set("voice", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ishita">Ishita</SelectItem><SelectItem value="anushka">Anushka</SelectItem></SelectContent></Select>
                  <Button variant="outline" onClick={() => toast.info("Voice preview (demo)")}><Volume2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <div><Label>Default Language</Label><Select value={agent.default_language} onValueChange={(v) => set("default_language", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="auto">Auto-detect</SelectItem><SelectItem value="hi-IN">Hindi</SelectItem><SelectItem value="gu-IN">Gujarati</SelectItem></SelectContent></Select></div>
              <div><Label>Speaking Pace: {agent.speaking_pace}x</Label><Slider min={0.8} max={1.3} step={0.1} value={[agent.speaking_pace]} onValueChange={([v]) => set("speaking_pace", v)} className="mt-3" /></div>
            </Card>
          </div>

          <Card className="border shadow-none p-6 mt-6" data-testid="persona-business-prompt-card" style={{ borderColor: "rgba(79,70,229,0.35)", background: "rgba(79,70,229,0.04)" }}>
            <div className="flex items-center gap-2 mb-1"><MessageCircleQuestion className="w-4 h-4 text-indigo-500" /><h3 className="font-semibold font-display">Your Business & Product Prompt</h3></div>
            <p className="text-xs text-slate-500 mb-3">Tell {agent.name} exactly what your business does, your products/services, pricing rules, offers, tone and do's & don'ts. This is injected into the AI on every call and test — the more specific, the better your call outcomes.</p>
            <textarea value={agent.custom_prompt || ""} onChange={(e) => set("custom_prompt", e.target.value)}
              placeholder="e.g. We are Anton Automation, we sell industrial PLC & SCADA automation systems to factories in Gujarat. Always greet warmly, ask what machine they want to automate, mention our 10-year support, never quote exact prices — instead book a site visit. Use {lead_name} and {interest}. If the caller is busy, politely offer to call back..."
              className="w-full min-h-[180px] rounded-md border px-3 py-2 text-sm bg-transparent" data-testid="agent-custom-prompt" />
            <p className="mt-2 text-[11px] text-slate-400">Click "Save Changes" above after editing, then use the Test tab to hear it on a real call.</p>
          </Card>
        </TabsContent>

        <TabsContent value="outbound">
          <Card className="border shadow-none p-6 space-y-4 max-w-3xl">
            <div><Label>Opening Greeting (use {"{name}"} & {"{interest}"})</Label><Textarea value={agent.outbound_greeting} onChange={(e) => set("outbound_greeting", e.target.value)} rows={3} /></div>
            <div><Label className="mb-2 block">Qualification Questions & Scoring</Label>
              {agent.questions?.map((q, i) => (
                <div key={q.id} className="flex gap-2 mb-2 items-center"><span className="text-xs w-5 text-slate-400">{i+1}</span><Input value={q.text} onChange={(e) => { const qs = [...agent.questions]; qs[i] = { ...q, text: e.target.value }; set("questions", qs); }} /><Input type="number" className="w-16" value={q.weight} onChange={(e) => { const qs = [...agent.questions]; qs[i] = { ...q, weight: Number(e.target.value) }; set("questions", qs); }} /></div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="inbound">
          <Card className="border shadow-none p-6 space-y-4 max-w-3xl">
            <div><Label>Inbound Greeting</Label><Textarea value={agent.inbound_greeting} onChange={(e) => set("inbound_greeting", e.target.value)} rows={3} /></div>
            <div className="text-sm text-slate-500">Same 5 qualification questions auto-trigger after the caller states their purpose.</div>
            <div><Label>After-hours Message</Label><Textarea defaultValue="Hum abhi available nahi hain. Kripya business hours mein call karein." rows={2} /></div>
            <div><Label>Hold Message</Label><Textarea defaultValue="Kripya thodi der wait karein, main aapki madad karti hoon." rows={2} /></div>
          </Card>
        </TabsContent>

        <TabsContent value="behaviour">
          <Card className="border shadow-none p-6 space-y-4 max-w-2xl">
            <div><Label>Human handover when score ≥ {agent.behaviour?.handover_score}</Label><Slider min={50} max={100} step={5} value={[agent.behaviour?.handover_score || 80]} onValueChange={([v]) => set("behaviour", { ...agent.behaviour, handover_score: v })} className="mt-3" /></div>
            <div><Label>Max silence before re-prompt (secs)</Label><Input type="number" className="w-24" value={agent.behaviour?.max_silence_secs || 5} onChange={(e) => set("behaviour", { ...agent.behaviour, max_silence_secs: Number(e.target.value) })} /></div>
            <div><Label>Language switch sensitivity</Label><Select value={agent.behaviour?.language_switch_sensitivity || "medium"} onValueChange={(v) => set("behaviour", { ...agent.behaviour, language_switch_sensitivity: v })}><SelectTrigger className="w-40"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
            <div className="flex items-center justify-between"><Label>Angry caller → immediate human transfer</Label><Switch checked={agent.behaviour?.angry_transfer} onCheckedChange={(v) => set("behaviour", { ...agent.behaviour, angry_transfer: v })} /></div>
          </Card>
        </TabsContent>

        <TabsContent value="knowledge"><KnowledgeTab onTest={() => setTab("test")} /></TabsContent>

        <TabsContent value="test"><TestSimulator agent={agent} /></TabsContent>

        <TabsContent value="performance">
          {perf && (
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border shadow-none p-5"><h3 className="font-semibold font-display text-sm mb-4">Call Quality Trend (Out vs In)</h3>
                <ResponsiveContainer width="100%" height={240}><LineChart data={perf.quality_trend}><CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="day" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend /><Line dataKey="outbound" stroke="#3B82F6" strokeWidth={2} /><Line dataKey="inbound" stroke="#A855F7" strokeWidth={2} /></LineChart></ResponsiveContainer></Card>
              <Card className="border shadow-none p-5"><h3 className="font-semibold font-display text-sm mb-4">Accuracy by Language</h3>
                <ResponsiveContainer width="100%" height={240}><BarChart data={perf.accuracy_by_language.map(d=>({...d, language: LANG_NAMES[d.language]||d.language}))}><CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} /><XAxis dataKey="language" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend /><Bar dataKey="outbound" fill="#3B82F6" radius={[4,4,0,0]} /><Bar dataKey="inbound" fill="#A855F7" radius={[4,4,0,0]} /></BarChart></ResponsiveContainer></Card>
              <Card className="border shadow-none p-6 lg:col-span-2">
                <h3 className="font-semibold font-display text-sm mb-2">Prompt Improvement Suggestions</h3>
                <ul className="text-sm text-slate-500 space-y-1 list-disc pl-5"><li>Ask for timeline earlier — most missed criterion.</li><li>Add regional greeting variants for Tamil callers.</li><li>Reduce silence timeout to 4s for higher engagement.</li></ul>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TestSimulator({ agent }) {
  const [dir, setDir] = useState("outbound");
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(5);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const start = async () => {
    setMsgs([]); setScore(0);
    const { data } = await api.post("/agent/test", { message: "", direction: dir, history: [] });
    setMsgs([{ role: "akriti", text: data.reply }]); setTotal(data.total);
  };

  const send = async () => {
    if (!input.trim()) return;
    const history = msgs.map((m) => ({ role: m.role === "akriti" ? "assistant" : "user", content: m.text }));
    history.push({ role: "user", content: input });
    const nm = [...msgs, { role: "user", text: input }];
    setMsgs(nm); setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/agent/test", { message: input, direction: dir, history });
      setMsgs([...nm, { role: "akriti", text: data.reply }]); setScore(data.score); setTotal(data.total);
    } finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="border shadow-none lg:col-span-2 flex flex-col h-[520px]">
        <div className="p-4 border-b flex items-center gap-3">
          <Select value={dir} onValueChange={(v) => { setDir(v); setMsgs([]); }}><SelectTrigger className="w-40" data-testid="test-direction"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="outbound">Test Outbound</SelectItem><SelectItem value="inbound">Test Inbound</SelectItem></SelectContent></Select>
          <Button size="sm" variant="outline" onClick={start} data-testid="test-start"><Play className="w-3 h-3 mr-1" />Start Call</Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {msgs.length === 0 && <div className="text-center text-slate-400 text-sm mt-16">Click "Start Call" to simulate a conversation with Akriti.</div>}
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${m.role === "user" ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800"}`}>{m.text}</div>
            </div>
          ))}
          {loading && <div className="text-xs text-slate-400">Akriti is typing...</div>}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type as the caller..." data-testid="test-input" />
          <Button onClick={send} disabled={loading || !msgs.length} data-testid="test-send"><Send className="w-4 h-4" /></Button>
        </div>
      </Card>
      <div className="lg:col-span-1 space-y-6">
        <PhoneTestCard />
        <Card className="border shadow-none p-6">
          <h3 className="font-semibold font-display text-sm mb-4">Live Score Tracker</h3>
          <div className="text-4xl font-bold font-display text-indigo-600">{score}<span className="text-lg text-slate-400">/100</span></div>
          <Progress value={score} className="mt-3" />
          <div className="mt-4 text-sm text-slate-500">Criteria collected: {Math.round(score/20)} of {total}</div>
          <div className="mt-2 text-xs text-slate-400">Category: {score >= 80 ? "HOT 🔥" : score >= 50 ? "WARM" : "COLD"}</div>
        </Card>
      </div>
    </div>
  );
}

function PhoneTestCard() {
  const [phone, setPhone] = useState("");
  const [calling, setCalling] = useState(false);
  const [last, setLast] = useState(null);
  const [detail, setDetail] = useState(null);
  const call = async () => {
    if (!phone.trim()) { toast.error("Enter your phone number"); return; }
    setCalling(true); setLast(null); setDetail(null);
    try {
      const { data } = await api.post("/agent/test-call", { phone });
      console.log("[TEST-CALL] request body sent to Railway /dial-out:", data.request);
      console.log("[TEST-CALL] Railway response:", { status: data.railway_status, body: data.railway_response });
      setLast(data.message);
      setDetail({ request: data.request, status: data.railway_status, response: data.railway_response, ok: data.dispatched });
      (data.dispatched ? toast.success : toast.error)(data.message);
    } catch (e) { toast.error(apiErr(e)); } finally { setCalling(false); }
  };
  return (
    <Card className="border shadow-none p-6" data-testid="phone-test-card" style={{ borderColor: "rgba(79,70,229,0.35)", background: "rgba(79,70,229,0.04)" }}>
      <div className="flex items-center gap-2 mb-1"><PhoneCall className="w-4 h-4 text-indigo-500" /><h3 className="font-semibold font-display text-sm">Call my phone</h3></div>
      <p className="text-xs text-slate-500 mb-3">Hear your live AI agent on a real call before going live. Re-run this after every script or knowledge change.</p>
      <Label className="text-xs">Your phone number</Label>
      <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98123 45678" className="mb-3" data-testid="phone-test-input" />
      <Button className="w-full" style={{ background: "#4f46e5" }} onClick={call} disabled={calling} data-testid="phone-test-call">
        {calling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PhoneCall className="w-4 h-4 mr-1" />}Call me now
      </Button>
      {last && <div className={`mt-3 text-xs ${detail?.ok ? "text-emerald-600" : "text-amber-600"}`} data-testid="phone-test-result">{last}</div>}
      {detail && (
        <div className="mt-3 space-y-2" data-testid="phone-test-detail">
          <div>
            <div className="text-[11px] font-semibold text-slate-500 mb-1">Request sent to Railway /dial-out</div>
            <pre className="text-[10px] leading-relaxed bg-slate-900 text-slate-100 rounded-md p-2 overflow-x-auto max-h-40" data-testid="phone-test-request">{JSON.stringify(detail.request, null, 2)}</pre>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-slate-500 mb-1">Railway response {detail.status != null && <span className={`px-1.5 py-0.5 rounded-full ${detail.ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>HTTP {detail.status}</span>}</div>
            <pre className="text-[10px] leading-relaxed bg-slate-900 text-slate-100 rounded-md p-2 overflow-x-auto max-h-40" data-testid="phone-test-response">{typeof detail.response === "string" ? detail.response : JSON.stringify(detail.response, null, 2)}</pre>
          </div>
        </div>
      )}
      <p className="mt-3 text-[11px] text-slate-400">Requires Plivo connected in Integrations. This places a real outbound call.</p>
      <TestCallHistory refresh={last} />
    </Card>
  );
}

function TestCallHistory({ refresh }) {
  const [calls, setCalls] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/agent/test-calls").then((r) => setCalls(r.data)).catch(() => setCalls([]));
  useEffect(() => { load(); }, [refresh]);
  if (!calls.length) return null;
  const fmt = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
  return (
    <div className="mt-4 pt-4 border-t" data-testid="test-call-history">
      <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Recent test calls</div>
      <div className="space-y-2">
        {calls.map((c) => (
          <div key={c.id} className="text-xs rounded-lg border p-2" data-testid={`test-call-${c.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">{c.mobile}</span>
              <span className="text-slate-400">{fmt(c.started_at)}</span>
            </div>
            <div className="text-slate-500">{c.outcome || c.status || "—"}</div>
            {c.recording_url && <audio controls src={c.recording_url} className="w-full mt-1 h-8" data-testid={`test-call-audio-${c.id}`} />}
            {c.transcript && (
              <button className="text-indigo-600 mt-1" onClick={() => setOpen(open === c.id ? null : c.id)} data-testid={`test-call-transcript-${c.id}`}>
                {open === c.id ? "Hide transcript" : "View transcript"}
              </button>
            )}
            {open === c.id && c.transcript && <div className="mt-1 text-slate-500 whitespace-pre-wrap bg-slate-50 dark:bg-white/5 rounded p-2 max-h-40 overflow-y-auto">{c.transcript}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
